import { useCallback, useEffect, useMemo, useState } from 'react';

import { Pack, ProgramOrGuideItem } from '@/components/app/cards/PacksList/types';
import { INITIAL_AUDIO_STATE } from '@/components/emotion-detector/AudioPlayer/constants';
import { CurrentAudio } from '@/components/emotion-detector/AudioPlayer/types';
import { CONTENT_TYPES, RECOMMENDATIONS } from '@/components/emotion-detector/constants';
import { useApi } from '@/hooks/api';
import useSearch from '@/hooks/app/useSearch';
import { EmotionUtils, TopEmotion } from '@/utils/emotion-detector';
import { setupAndPlayAudio } from '@/utils/emotion-detector/audioPlayerHelpers';
import {
	extractGuidePlayData,
	extractProgramPlayData,
	fetchGuideData,
	fetchProgramData,
} from '@/utils/emotion-detector/contentApi';

export function useEmotionRecommendations(emotion: TopEmotion | null) {
	const apiRequest = useApi();

	// Simple audio player state for emotion detector
	const [currentAudio, setCurrentAudio] = useState<CurrentAudio>(INITIAL_AUDIO_STATE);

	// Use search hook for content recommendations based on emotion
	const searchQuery = useMemo(() => {
		return emotion ? EmotionUtils.getSearchQuery(emotion) : undefined;
	}, [emotion]);

	const { pack: searchPack, isLoading: searchLoading } = useSearch(
		searchQuery,
		['meditations', 'daily_programs'],
		[],
	);

	// Process search results to create recommendation pack with unlocked content
	const [recommendationPack, setRecommendationPack] = useState<Pack | null>(null);

	useEffect(() => {
		if (searchPack && emotion) {
			// Filter to audio-only content
			const audioOnlyItems = searchPack.items.filter(item => {
				// For guides: check asset_type
				if ('asset_type' in item && item.asset_type === CONTENT_TYPES.AUDIO) {
					return true;
				}
				// For programs: assume they contain audio content (since we're filtering by meditations/daily_programs)
				if ('type' in item && item.type === CONTENT_TYPES.PROGRAM) {
					return true;
				}
				// Fallback: check image.type
				if (
					'image' in item &&
					item.image &&
					typeof item.image === 'object' &&
					'type' in item.image &&
					item.image.type === CONTENT_TYPES.AUDIO
				) {
					return true;
				}
				return false;
			});

			// Deduplicate by title - only keep unique titles
			const uniqueByTitle = audioOnlyItems.filter((item, index, array) => {
				if (!('title' in item) || !item.title) return false;

				// Keep only the first item found for each title
				return array.findIndex(otherItem => 'title' in otherItem && otherItem.title === item.title) === index;
			});

			// If we have less than MIN_ITEMS unique items, fill with random selections from all audio items
			let finalItems = uniqueByTitle;
			if (finalItems.length < RECOMMENDATIONS.MIN_ITEMS && audioOnlyItems.length > finalItems.length) {
				const remainingItems = audioOnlyItems.filter(item => {
					// Don't pick items we already selected (by content_id or guide_id/program_id)
					return !finalItems.some(selected => {
						if ('content_id' in selected && 'content_id' in item) {
							return selected.content_id === item.content_id;
						}
						if ('guide_id' in selected && 'guide_id' in item) {
							return selected.guide_id === item.guide_id;
						}
						if ('program_id' in selected && 'program_id' in item && selected.program_id === item.program_id) {
							// Only match programs if they're both programs (not program vs guide)
							return selected.type === item.type && selected.type === CONTENT_TYPES.PROGRAM;
						}
						return false;
					});
				});

				// Randomly select additional items to reach MIN_ITEMS total
				const needed = Math.min(RECOMMENDATIONS.MIN_ITEMS - finalItems.length, remainingItems.length);
				const shuffled = [...remainingItems].sort(
					() => Math.random() - RECOMMENDATIONS.SHUFFLE_RANDOM_FACTOR,
				);
				finalItems = [...finalItems, ...shuffled.slice(0, needed)];
			}

			const unlockedPack: Pack = {
				...searchPack,
				items: finalItems.map(item => ({
					...item,
					is_unlocked: true, // Override to make all content available
					promotional_text: '', // Add required promotional_text property
				})),
			};
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setRecommendationPack(unlockedPack);
		} else {
			setRecommendationPack(null);
		}
	}, [searchPack, emotion]);

	const handleContentClick = useCallback(
		async (contentId?: string) => {
			if (!contentId || !recommendationPack) {
				return;
			}

			// Find the content item
			const contentItem = recommendationPack.items.find(item => {
				const programOrGuideItem = item as ProgramOrGuideItem;
				return programOrGuideItem.guide_id === contentId || programOrGuideItem.program_id === contentId;
			}) as ProgramOrGuideItem;

			if (!contentItem) {
				return;
			}

			try {
				if (contentItem.guide_id) {
					// Fetch and play guide content
					try {
						const apiResponse = await fetchGuideData(contentItem.guide_id, apiRequest);
						const playData = extractGuidePlayData(apiResponse, contentItem.image?.url);

						if (playData) {
							await setupAndPlayAudio({
								...playData,
								currentAudio,
								setCurrentAudio,
							});
						}
					} catch (apiError) {
						// Fallback: Try to use assets directly from search results
						const itemWithAssets = contentItem as ProgramOrGuideItem & {
							assets?: Array<{ type: string; duration?: number; url: string }>;
						};
						const audioAsset = itemWithAssets.assets?.find(asset => asset.type === CONTENT_TYPES.AUDIO);

						if (audioAsset) {
							await setupAndPlayAudio({
								audioUrl: audioAsset.url,
								title: contentItem.title,
								thumbnailUrl: contentItem.image?.url,
								programTitle: contentItem.narrator_name,
								currentAudio,
								setCurrentAudio,
							});
						}
					}
				} else if (contentItem.program_id) {
					// Fetch and play program content
					try {
						const apiResponse = await fetchProgramData(contentItem.program_id, apiRequest);
						const playData = extractProgramPlayData(apiResponse, contentItem.title, contentItem.image?.url);

						if (playData) {
							await setupAndPlayAudio({
								...playData,
								currentAudio,
								setCurrentAudio,
							});
						}
					} catch (apiError) {
						// Program API error, ignore silently
					}
				}
			} catch (error) {
				// Content click error, ignore silently
			}
		},
		[recommendationPack, apiRequest, currentAudio, setCurrentAudio],
	);

	return {
		// State
		recommendationPack,
		searchLoading,
		currentAudio,

		// Actions
		setCurrentAudio,
		handleContentClick,
	};
}
